import {useEffect, useRef, useState} from 'react';

/** Keep the browser's file URL alive long enough to consume it or accept a manual save click. */
export function useBrowserFileDownload(scope: string | null) {
  const [file, setFile] = useState<{url: string; name: string; fallbackUrl?: string} | null>(null);
  const mounted = useRef(true);
  const scopeState = useRef({scope, generation: 0});
  if (scopeState.current.scope !== scope) {
    scopeState.current = {scope, generation: scopeState.current.generation + 1};
  }
  const generation = scopeState.current.generation;
  const urls = useRef(new Map<string, number>());

  function release(url: string) {
    const timeout = urls.current.get(url);
    if (timeout === undefined) return;
    window.clearTimeout(timeout);
    urls.current.delete(url);
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const url of urls.current.keys()) release(url);
    };
  }, []);

  useEffect(() => {
    for (const url of urls.current.keys()) release(url);
    setFile(null);
  }, [scope]);

  return {
    startDownload(blob: Blob, name: string, fallbackUrl?: string) {
      if (!mounted.current || generation !== scopeState.current.generation) return;
      if (fallbackUrl && !/^\/api\/v1\/fenzsafe\/(documents\/[a-zA-Z0-9-]+\/bytes|share\/[a-f0-9]{64}\/documents\/[a-zA-Z0-9-]+\/bytes)$/.test(fallbackUrl)) {
        throw new Error('The download link is not available.');
      }
      const url = URL.createObjectURL(blob);
      const timeout = window.setTimeout(() => {
        release(url);
        setFile((current) => current?.url === url ? null : current);
      }, 60_000);
      urls.current.set(url, timeout);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      anchor.rel = 'noreferrer';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } catch (error) {
        release(url);
        throw error;
      } finally {
        anchor.remove();
      }
      setFile({url, name, fallbackUrl});
    },
    downloadStatus: file ? (
      <p role="status">
        Download requested. If no file appeared,{' '}
        <a style={{color: 'inherit', textDecoration: 'underline'}} href={file.fallbackUrl ?? file.url} download={file.name} target="_blank" rel="noreferrer" referrerPolicy="no-referrer">save {file.name}</a>.
        {' '}This save link is available for one minute; you can use Download again afterward.
        {file.fallbackUrl && ' If a sign-in or unavailable-file page opens, sign in again or ask the sender for a new link.'}
      </p>
    ) : null,
  };
}
